import React from 'react';
import Papa from 'papaparse';

/**
 * Export aggregated timeline data (minutes/hourly/daily/monthly) to CSV.
 *
 * @param {Array<{date: string, rate: number}>} timelineData - Normalized data you feed to the chart.
 * @param {string} eventName - Used for file naming.
 * @param {Object} [opts]
 * @param {"minutes"|"hourly"|"daily"|"monthly"} [opts.timeRange="daily"] - For filename + first column label.
 * @param {Date|string|null} [opts.startDate=null] - Optional: included as metadata columns.
 * @param {Date|string|null} [opts.endDate=null]   - Optional: included as metadata columns.
 */
const exportTimelineToCSVRow = (
    timelineData,
    eventName,
    opts = {}
) => {
    const {
        timeRange = 'daily',
        startDate = null,
        endDate = null,
    } = opts;

    if (!Array.isArray(timelineData) || timelineData.length === 0) {
        console.warn('No timeline data available to export.');
        return;
    }

    // Decide first column label based on granularity
    const bucketLabel =
        timeRange === 'hourly'
            ? 'Time'
            : timeRange === 'monthly'
                ? 'Month'
                : timeRange === 'minutes'
                    ? 'Minute'
                    : 'Date';

    // Normalize metadata values for CSV (ISO if Date objects are passed)
    const toIso = (d) =>
        d instanceof Date ? d.toISOString() : (d || '');

    const csvData = timelineData.map((entry) => ({
        eventName,
        timeRange,
        startDate: toIso(startDate),
        endDate: toIso(endDate),
        [bucketLabel]: entry?.date ?? '',
        registrations: entry?.rate ?? 0,
    }));

    // Generate CSV with PapaParse
    const csv = Papa.unparse(csvData);

    // Build friendly filename: <eventName>-feedback-<timeRange>-<YYYY-MM-DD>.csv
    const yyyyMmDd = new Date().toISOString().slice(0, 10);
    const safeEventName = (eventName || 'event').replace(/[^\w\-]+/g, '_');
    const fileName = `${safeEventName}-feedback-${timeRange}-${yyyyMmDd}.csv`;

    // Trigger download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};

export default exportTimelineToCSVRow;
